import React from 'react'
import HouseImg from '../assets/houseimg.svg'
import UserInfo from '../assets/useraddimg.svg'
import NoteImg from '../assets/noteimg.svg'
import { isFilingBetaUser } from '../config/filingBetaEmails'

interface FilingModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectOption: (option: string) => void
  /**
   * Current user's email. Used to bypass the "Coming Soon" gating for beta
   * testers listed in `filingBetaEmails`.
   */
  userEmail?: string | null | undefined
}

interface FilingOption {
  id: string
  title: string
  icon: React.ReactNode
  comingSoon: boolean
}

const FilingModal: React.FC<FilingModalProps> = ({
  isOpen,
  onClose,
  onSelectOption,
  userEmail,
}) => {
  if (!isOpen) return null

  const isBeta = isFilingBetaUser(userEmail)

  const filingOptions: FilingOption[] = [
    {
      id: 'ct600-filing',
      title: 'Corporation Tax and Company Accounts',
      icon: <img src={HouseImg} alt='' />,
      comingSoon: false,
    },
    {
      id: 'self-assessment-sa',
      title: 'Self Assessment (Non-MTD)',
      icon: <img src={UserInfo} alt='' />,
      comingSoon: false,
    },
    // Temporarily hidden — Self Assessment (MTD) not offered yet.
    // {
    //   id: 'self-assessment-trad',
    //   title: 'Self Assessment (MTD)',
    //   icon: <img src={UserInfo} alt='' />,
    //   comingSoon: true,
    // },
    {
      id: 'confirmation-statement-filing',
      title: 'Confirmation Statement',
      icon: <img src={NoteImg} alt='' />,
      comingSoon: false,
    },
  ]

  return (
    <div
      className='fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50'
      style={{ backdropFilter: 'blur(4px)' }}
    >
      <div className='bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-auto relative'>
        {/* Header */}
        <div className='px-8 pt-8 pb-6 border-b border-gray-100'>
          <div className='flex items-center justify-between'>
            <div>
              <h2 className='text-3xl font-bold text-gray-900 mb-2 text-start'>
                Filing Option
              </h2>
              <p className='text-gray-500 text-lg'>
                Select your filing choice below
              </p>
            </div>
            <button
              onClick={onClose}
              className='w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors'
            >
              <svg
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M18 6L6 18M6 6L18 18'
                  stroke='#6B7280'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Filing Options */}
        <div className='px-8 py-6 space-y-3'>
          {filingOptions.map(option => {
            const isDisabled = option.comingSoon && !isBeta
            return (
              <button
                key={option.id}
                onClick={() => {
                  if (isDisabled) return
                  onSelectOption(option.id)
                }}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                className={`w-full flex items-center justify-between gap-3 p-3 rounded-2xl transition-all text-left ${
                  isDisabled
                    ? 'bg-gray-50 cursor-not-allowed'
                    : 'hover:bg-gray-50 cursor-pointer'
                }`}
              >
                <div className='flex items-center gap-4 min-w-0 flex-1'>
                  <div
                    className={`rounded-full w-2 h-2 flex-shrink-0 ${
                      isDisabled ? 'bg-gray-300' : 'bg-[#A4A7AE]'
                    }`}
                  />
                  <span
                    className={`text-base font-medium whitespace-nowrap truncate ${
                      isDisabled ? 'text-gray-400' : 'text-gray-900'
                    }`}
                  >
                    {option.title}
                  </span>
                  {isDisabled && (
                    <span className='flex-shrink-0 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700'>
                      Coming Soon
                    </span>
                  )}
                </div>
                <div
                  className={`flex-shrink-0 ${
                    isDisabled ? 'opacity-40 grayscale' : ''
                  }`}
                >
                  {option.icon}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default FilingModal
